/*eslint import/namespace: [2, { allowComputed: true }]*/
import React, { FunctionComponent, useEffect } from 'react';
import {
  getFlags,
  initializeGoogleAnalytics,
  logError,
} from '@foxtrotplatform/platform-micro-site-core';
import { createHashRouter, RouterProvider } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { lazily } from 'react-lazily';

import './index.css';
import { CustomerSignupAppConfig } from './';
import { routes } from './routes';

const { ErrorAlert, PlatformProvider } = lazily(
  () => import('@foxtrotplatform/platform-micro-site-core')
);

const { CopyProvider } = lazily(
  () => import('@foxtrotplatform/developer-platform-common-ui-component-lib')
);

const { FlagsProvider } = lazily(() => import('react-feature-flags'));

const { ErrorBoundary } = lazily(() => import('react-error-boundary'));

const { AppWrapper } = lazily(() => import('./AppWrapper'));

const { SignUpProvider } = lazily(
  () => import('./common/provider/SignUpProvider')
);
const { LocationHistoryProvider } = lazily(
  () => import('./common/provider/LocationHistoryProvider')
);

interface AppProps {
  config: CustomerSignupAppConfig;
}

const Root: FunctionComponent = () => (
  <ErrorBoundary FallbackComponent={ErrorAlert} onError={logError}>
    <LocationHistoryProvider>
      <SignUpProvider>
        <CopyProvider>
          <AppWrapper />
        </CopyProvider>
      </SignUpProvider>
    </LocationHistoryProvider>
  </ErrorBoundary>
);

const router = createHashRouter([
  {
    path: '/',
    element: <Root />,
    children: routes,
  },
]);

export const App: React.FunctionComponent<AppProps> = ({ config }) => {
  useEffect(() => {
    initializeGoogleAnalytics(config.googleAnalyticsTrackingCode as string);
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname + window.location.search,
    });
  }, [config.googleAnalyticsTrackingCode]);

  const featureFlags = getFlags(config?.featureFlags);

  return (
    <PlatformProvider configuration={config}>
      <FlagsProvider value={featureFlags}>
        <RouterProvider router={router} />
      </FlagsProvider>
    </PlatformProvider>
  );
};
